import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useIntl } from "react-intl";
import classNames from "classnames";
import autoCompleteKeywordRepository from "@/repository/autoCompleteKeywordRepository";
import { useAutocomplete } from "@/query/autocomplete";
import { useAgentId } from "@/hook";
import Button from "@/component/Button";
import HighlightText from "@/component/HighlightText";
import { SearchBar, SearchBarRef } from "@/component/input";
import { CloseIcon } from "@/assets/svgs/icons";


const AutocompleteTestPanel: React.FC<{ open: boolean, onClose: () => void }> = ({ open, onClose }) => {
    const intl = useIntl();
    const agentId = useAgentId();
    const location = useLocation();
    const match = location.pathname.match(/\/autocomplete\/([^/]+)/);
    const autocompleteId = match?.[1] ?? null;

    const searchBarRef = useRef<SearchBarRef>(null);
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
    const [query, setQuery] = useState<string>("");
    const [suggestions, setSuggestions] = useState<string[]>([]);

    const { data: autocomplete } = useAutocomplete(agentId, autocompleteId!);
    const token = autocomplete?.token;

    const handleSuggestionClick = (suggestion: string) => {
        searchBarRef.current?.setValue(suggestion);
        setQuery(suggestion);
        setSuggestions([]);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        switch (e.key) {
            case "ArrowDown":
                setSelectedIndex(prev => (prev === null ? 0 : Math.min(suggestions.length - 1, prev + 1)));
                break;
            case "ArrowUp":
                setSelectedIndex(prev => (prev === null ? 0 : Math.max(0, prev - 1)));
                break;
            case "Enter":
                if (selectedIndex === null) {
                    break;
                }
                searchBarRef.current?.setValue(suggestions[selectedIndex!]);
                setQuery(suggestions[selectedIndex!]);
                setSelectedIndex(null);
                break;
        }
    };

    useEffect(() => {
        if(!query){
            setSuggestions([]);
            return;
        }
        const fetchSuggestions = async () => {
            await autoCompleteKeywordRepository.findByKeyword(token!, query)
                .then(response => setSuggestions(response?.data || []))
                .catch(() => setSuggestions([]));
        };
        const debounceTimeout = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounceTimeout);
    }, [query, token]);

    if (!open) {
        return null;
    }
    return (
        <div className="test-panel">
            <div className="py-3 flex justify-between items-center">
                <div className="text-black-500 font-[500] text-[15px]">
                    {intl.formatMessage({ id: 'i08029' })}
                </div>

                <Button>
                    <CloseIcon className="size-4"
                               onClick={onClose}
                    />
                </Button>
            </div>

            <div className="flex items-center relative">
                <SearchBar ref={searchBarRef}
                           placeholder={intl.formatMessage({ id: "i00005" })}
                           onKeyDown={handleKeyDown}
                           onInput={e => setQuery(e.target.value)}
                />

                {suggestions.length > 0 && (
                    <ul className="absolute w-full top-full left-0 p-4 mt-2 flex flex-col gap-2 bg-white border border-black-200 rounded-lg shadow-lg z-10">
                        {suggestions.map((suggestion, index) => (
                            <li key={index}
                                className={classNames(
                                    "cursor-pointer hover:bg-gray-100 font-inter text-[13px] font-normal leading-[1.5] break-all",
                                    selectedIndex === index && "bg-gray-100"
                                )}
                                onClick={() => handleSuggestionClick(suggestion)}
                            >
                                <HighlightText query={suggestion}
                                               keyword={query}
                                               highlightClassName="text-point-red"
                                />
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AutocompleteTestPanel;
